import type { ImportQuickReply, MenuImportView } from './api.js'

/**
 * Pergunta pendente de um import em WAITING_INPUT (#300).
 *
 * O backend marca `awaiting` = input | photo e grava a pergunta como a última
 * mensagem do assistente (kind question | photo_request). Antes, a UI só
 * mostrava o cartão de progresso ("Sem progresso há Ns") e a pergunta ficava
 * perdida no chat — às vezes como um '📷' sem texto. Isto extrai o que deve
 * ser mostrado em destaque; `text: null` = sem texto legível (usar fallback).
 */
export type PendingPrompt = {
  kind: 'input' | 'photo'
  text: string | null
  page: number | null
  quickReplies: ImportQuickReply[]
}

export function pendingPrompt(
  view: Pick<MenuImportView, 'status' | 'awaiting' | 'messages'>,
): PendingPrompt | null {
  if (view.status !== 'processing' || view.awaiting === 'none') return null
  const wantedKind = view.awaiting === 'photo' ? 'photo_request' : 'question'
  const last = [...view.messages].reverse().find((m) => m.role === 'assistant')
  const msg = last && last.kind === wantedKind ? last : undefined
  const raw = msg?.content.trim() ?? ''
  // '📷' (ou só emoji/pontuação) não é pergunta — conteúdo legado do bug
  const text = /\p{L}/u.test(raw) ? raw : null
  return {
    kind: view.awaiting,
    text,
    page: view.awaiting === 'photo' ? (msg?.photo_page ?? null) : null,
    quickReplies: (msg?.quick_replies ?? []).map((qr) =>
      typeof qr === 'string' ? { label: qr, action: 'reply' as const } : qr,
    ),
  }
}

type PagesView = Pick<MenuImportView, 'page_count' | 'pages'>

/** Páginas que o dono pode refotografar no cartão de espera (#301). */
export function photoPageOptions(view: PagesView): number[] {
  const fromView = (view.pages ?? []).map((p) => p.n)
  if (fromView.length > 0) return fromView
  return Array.from({ length: Math.max(view.page_count, 0) }, (_, i) => i + 1)
}

/**
 * Página padrão do "Enviar nova foto" quando o servidor não pediu uma página
 * (pergunta em texto livre): a primeira com erro de leitura; senão a primeira.
 */
export function defaultPhotoPage(view: PagesView): number {
  const failed = (view.pages ?? []).find((p) => p.status === 'error')
  return failed?.n ?? photoPageOptions(view)[0] ?? 1
}
